import {
  Check,
  CheckCheck,
  Loader2,
  MessageSquare,
  SendHorizontal,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { toast } from '@/shared/lib/toast'
import { cn } from '@/shared/lib/utils'
import socketService from '@/shared/socket/socket.service'

import { useMessages } from '../hooks/useMessages'

function formatTime(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatDateHeader(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  const today = new Date()
  if (date.toDateString() === today.toDateString()) {
    return 'Today'
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

export function DirectMessageModal({ open, onOpenChange, partner }) {
  const {
    messages,
    isLoading,
    isSending,
    fetchConversation,
    sendMessage,
    markAsRead,
    setActivePartner,
    addMessage,
    updateMessageRead,
    clearConversation,
  } = useMessages()

  const [inputMessage, setInputMessage] = useState('')
  const scrollRef = useRef(null)
  const bottomRef = useRef(null)
  const inputRef = useRef(null)

  const partnerId = String(partner?._id || partner?.id || '')
  const partnerRole = partner?.role || 'user'
  const partnerName = partner?.name || 'User'
  const isPartnerAdmin = partnerRole === 'admin'

  const scrollToBottom = (behavior = 'smooth') => {
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight
      }
      bottomRef.current?.scrollIntoView({ behavior, block: 'end' })
    })
  }

  // Load conversation and mark read on modal open
  useEffect(() => {
    if (!open || !partnerId) {
      clearConversation()
      return
    }

    setActivePartner(partner)
    fetchConversation(partnerId).then(() => {
      setTimeout(() => {
        scrollToBottom('auto')
        inputRef.current?.focus()
      }, 50)
    })
    markAsRead(partnerId, partnerRole)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, partnerId, partnerRole])

  // Real-time socket listener dedicated to this open modal
  useEffect(() => {
    if (!open || !partnerId) return

    const socket = socketService.connect()
    if (!socket) return

    const handleModalNewMessage = (msg) => {
      const msgSenderId = String(msg?.senderId || '')
      const msgRecipientId = String(msg?.recipientId || '')

      const isMatch =
        msgSenderId === partnerId ||
        msgRecipientId === partnerId ||
        (isPartnerAdmin &&
          (msg?.recipientRole === 'admin' || msg?.senderRole === 'admin'))

      if (isMatch) {
        addMessage(msg)
        setTimeout(() => scrollToBottom('smooth'), 50)
      }
    }

    const handleModalSentMessage = (msg) => {
      addMessage(msg)
      setTimeout(() => scrollToBottom('smooth'), 50)
    }

    const handleModalMessageRead = ({ readBy }) => {
      if (String(readBy) === partnerId) {
        updateMessageRead(partnerId)
      }
    }

    socket.on('message:new', handleModalNewMessage)
    socket.on('message:sent', handleModalSentMessage)
    socket.on('message:read', handleModalMessageRead)

    return () => {
      socket.off('message:new', handleModalNewMessage)
      socket.off('message:sent', handleModalSentMessage)
      socket.off('message:read', handleModalMessageRead)
    }
  }, [
    open,
    partnerId,
    isPartnerAdmin,
    addMessage,
    updateMessageRead,
    markAsRead,
  ])

  // Auto-scroll whenever messages change
  useEffect(() => {
    if (!open) return
    scrollToBottom('smooth')
  }, [messages.length, open])

  const handleSend = async (e) => {
    if (e) e.preventDefault()
    const text = inputMessage.trim()
    if (!text || isSending || !partnerId) return

    try {
      setInputMessage('')
      inputRef.current?.focus()
      await sendMessage({
        recipientId: partnerId,
        recipientRole: partner?.role || (isPartnerAdmin ? 'admin' : undefined),
        message: text,
      })
      setTimeout(() => {
        scrollToBottom('smooth')
        inputRef.current?.focus()
      }, 50)
    } catch (error) {
      setInputMessage(text)
      toast.error(error?.message || 'Failed to send message')
      console.error('Failed to send message:', error)
    } finally {
      inputRef.current?.focus()
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(600px,85dvh)] w-[calc(100vw-1.5rem)] sm:w-[500px] max-w-[calc(100vw-1.5rem)] sm:max-w-[500px] flex-col overflow-hidden rounded-2xl sm:rounded-[28px] p-0 shadow-2xl border border-black/[0.08]">
        {/* Header */}
        <DialogHeader className="border-b border-black/[0.06] bg-slate-50/60 px-4 sm:px-6 py-3.5 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Avatar className="size-10 border border-indigo-100 shadow-sm">
                <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-violet-600 text-xs font-bold text-white">
                  {getInitials(partnerName)}
                </AvatarFallback>
              </Avatar>
              <div className="text-left">
                <DialogTitle className="flex items-center gap-2 text-base font-bold text-slate-900">
                  <span>{partnerName}</span>
                  <Badge
                    variant="outline"
                    className={cn(
                      'capitalize px-2 py-0 text-[10px] font-semibold tracking-wide',
                      partnerRole === 'admin'
                        ? 'border-purple-200 bg-purple-50 text-purple-700'
                        : partnerRole === 'sales'
                          ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
                          : 'border-blue-200 bg-blue-50 text-blue-700',
                    )}
                  >
                    {partnerRole}
                  </Badge>
                </DialogTitle>
                <p className="text-xs text-slate-500 truncate max-w-[260px]">
                  {partner?.email || partner?.phoneNumber || 'Direct Message'}
                </p>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Message Area */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-6 py-4 space-y-3.5 bg-gradient-to-b from-slate-50/30 to-white"
        >
          {isLoading ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
              <Loader2 className="size-6 animate-spin text-indigo-500" />
              <p className="text-xs font-medium">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center px-4">
              <div className="rounded-full bg-indigo-50 p-3.5 text-indigo-500 shadow-sm border border-indigo-100">
                <MessageSquare className="size-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  No messages yet
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Send a message to start the real-time conversation.
                </p>
              </div>
            </div>
          ) : (
            messages.map((msg, index) => {
              // If sender is partner -> message is incoming (LEFT)
              // If sender is NOT partner -> message is outgoing/mine (RIGHT)
              const isFromPartner =
                String(msg.senderId) === partnerId ||
                (isPartnerAdmin && msg.senderRole === 'admin')
              const isMine = !isFromPartner

              const showDate =
                index === 0 ||
                formatDateHeader(messages[index - 1].createdAt) !==
                  formatDateHeader(msg.createdAt)

              return (
                <div key={msg.id || msg._id || index} className="space-y-2">
                  {showDate && (
                    <div className="flex justify-center my-2">
                      <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-medium text-slate-500 shadow-2xs">
                        {formatDateHeader(msg.createdAt)}
                      </span>
                    </div>
                  )}

                  <div
                    className={cn(
                      'flex items-end gap-1.5',
                      isMine ? 'justify-end' : 'justify-start',
                    )}
                  >
                    {!isMine && (
                      <Avatar className="size-6 mb-1 shrink-0">
                        <AvatarFallback className="bg-slate-200 text-[10px] font-bold text-slate-700">
                          {getInitials(msg.senderName || partnerName)}
                        </AvatarFallback>
                      </Avatar>
                    )}

                    <div
                      className={cn(
                        'max-w-[78%] rounded-2xl px-4 py-2.5 shadow-2xs text-[13px] leading-relaxed break-words',
                        isMine
                          ? 'bg-brand-navy text-white rounded-br-xs'
                          : 'bg-slate-100 text-slate-900 border border-slate-200/80 rounded-bl-xs',
                      )}
                    >
                      <p className="whitespace-pre-wrap">{msg.message}</p>
                      <div
                        className={cn(
                          'mt-1 flex items-center justify-end gap-1 text-[10px]',
                          isMine ? 'text-slate-300' : 'text-slate-400',
                        )}
                      >
                        <span>{formatTime(msg.createdAt)}</span>
                        {isMine && (
                          <span>
                            {msg.isRead ? (
                              <CheckCheck className="size-3 text-sky-400" />
                            ) : (
                              <Check className="size-3 text-slate-300" />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input Bar */}
        <form
          onSubmit={handleSend}
          className="border-t border-black/[0.06] bg-white p-3.5 flex items-center gap-2"
        >
          <input
            ref={inputRef}
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Message ${partnerName}...`}
            className="flex-1 rounded-full border border-slate-200 bg-slate-50/70 px-4 py-2.5 text-xs text-slate-900 outline-none transition focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-100"
            autoFocus
          />
          <Button
            type="submit"
            size="icon"
            disabled={!inputMessage.trim() || isSending}
            className="size-9 rounded-full bg-brand-navy text-white hover:bg-brand-navy-soft disabled:opacity-40 transition-transform active:scale-95 shrink-0"
          >
            {isSending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <SendHorizontal className="size-4" />
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
